import { loadStripe } from '@stripe/stripe-js'
import { useRouter } from 'next/router'
import useIsMobile from '@/util/useIsMobile'
import { useRecoilValue } from 'recoil'
import { subDataState, userInfoState } from '@/util/states'
import { setDoc, doc } from 'firebase/firestore'
import { db } from '@/util/firebase'
import styled from 'styled-components'

const stripePromise = loadStripe(`${process.env.NEXT_PUBLIC_STRIPE_PUBLISHABLE_KEY!}`)

const PrimaryBtn = styled.button<{ disabled?: boolean }>`
  width: 100%;
  padding: 16px 24px;
  margin-bottom: 12px;
  border-radius: 8px;
  border: none;
  background: ${props => props.disabled ? 'rgba(255,255,255,0.1)' : 'linear-gradient(135deg, #6B8E23 0%, #556B2F 100%)'};
  color: white;
  font-size: 15px;
  font-family: 'Soehne-Kraftig', sans-serif;
  letter-spacing: 0.5px;
  cursor: ${props => props.disabled ? 'not-allowed' : 'pointer'};
  opacity: ${props => props.disabled ? 0.5 : 1};
  transition: all 0.2s ease;
  text-align: center;
  display: flex;
  justify-content: center;
  align-items: center;
  
  &:hover:not(:disabled) {
    transform: translateY(-2px);
    box-shadow: 0 6px 20px rgba(107, 142, 35, 0.4);
    background: linear-gradient(135deg, #7BA428 0%, #6B8E23 100%);
  }
  
  &:active:not(:disabled) {
    transform: translateY(0);
  }
`

const SecondaryBtn = styled.button<{ disabled?: boolean }>`
  width: 100%;
  padding: 16px 24px;
  border-radius: 8px;
  border: 2px solid rgba(255,255,255,0.3);
  background: ${props => props.disabled ? 'rgba(255,255,255,0.05)' : 'transparent'};
  color: white;
  font-size: 14px;
  font-family: 'Soehne-Kraftig', sans-serif;
  letter-spacing: 0.5px;
  cursor: ${props => props.disabled ? 'not-allowed' : 'pointer'};
  opacity: ${props => props.disabled ? 0.5 : 1};
  transition: all 0.2s ease;
  text-align: center;
  display: flex;
  justify-content: center;
  align-items: center;
  
  &:hover:not(:disabled) {
    border-color: rgba(255,255,255,0.6);
    background: rgba(255,255,255,0.1);
    transform: translateY(-2px);
  }
  
  &:active:not(:disabled) {
    transform: translateY(0);
  }
`

const ButtonWrapper = styled.div`
  width: 100%;
  margin-bottom: 8px;
`

export default function Checkout({
  planName,
  monthlyPrice,
  yearlyPrice
}: {
  planName: string
  monthlyPrice: number | ''
  yearlyPrice: number | ''
}) {

  const isMobile = useIsMobile()
  const userInfo = useRecoilValue(userInfoState)
  const router = useRouter()
  
  const subData = useRecoilValue(subDataState)
  const userData = useRecoilValue(userInfoState)
  
  if (isMobile === undefined) return null
  if (subData === 'pending') return null

  const handleCheckout = async (isYearly: boolean) => {
    if (userInfo === 'pending') return
    if (!userInfo) {
      router.push({
        pathname: '/signup',
        query: { returnUrl: router.asPath }
      })
      return
    }
    
    if (subData?.isSubbed) {
      handleUpdate(isYearly)
      return
    }

    let priceId: string
    if (planName === 'Standard') {
      if (isYearly) priceId = process.env.STANDARD_YEARLY_ID!
      else priceId = process.env.STANDARD_MONTHLY_ID!
    } else {
      if (isYearly) priceId = process.env.BEYOND_YEARLY_ID!
      else priceId = process.env.BEYOND_MONTHLY_ID!
    }

    const { sessionId } = await fetch('/api/checkout', {
      method: 'POST',
      headers: {
        'content-type': 'application/json'
      },
      body: JSON.stringify({
        priceId: priceId,
        pathname: router.pathname
      })
    }).then((res) => res.json())

    const storeData = async (sessionId: string, uid: string) => {
      try {
        let isDocSet = false
        await setDoc(
          doc(db, 'users', uid), {
            sessionId: sessionId
          }, { merge: true }
        ).then(() => isDocSet = true)
        return isDocSet

      } catch (error) {
        console.error('Error adding document: ', error)
        return false
      }
    }

    const isStored = await storeData(sessionId, userInfo.uid)
    if (!isStored) {
      console.error('error: could not store checkout sessionId to firestore')
      return
    }

    const stripe = await stripePromise
    if (stripe) {
      const { error } = await stripe.redirectToCheckout({ sessionId })
      console.error('stripe returned an error: ', error)
    } else {
      console.error('the stripe object was null')
    }
  }

  const handleUpdate = (isYearly: boolean) => {
    let priceId: string
    let newSubType = ''

    if (planName === 'Standard') {
      if (isYearly) {
        priceId = process.env.STANDARD_YEARLY_ID!
        newSubType += 'Standard Edition (billed yearly)'
      }
      else {
        priceId = process.env.STANDARD_MONTHLY_ID!
        newSubType += 'Standard Edition (billed monthly)'
      }
    }
    else {
      if (isYearly) {
        priceId = process.env.BEYOND_YEARLY_ID!
        newSubType += 'Beyond Edition (billed yearly)'
      }
      else {
        priceId = process.env.BEYOND_MONTHLY_ID!
        newSubType += 'Beyond Edition (billed monthly)'
      }
    }

    let previewProration: boolean = true
    if (subData.tier === 'Beyond' && subData.monthlyOrYearly === 'Yearly') previewProration = false
    if (
      (subData.tier === 'Beyond' && subData.monthlyOrYearly === 'Monthly')
      && (planName === 'Standard' && !isYearly)
    ) previewProration = false
    if (
      (subData.tier === 'Standard' && subData.monthlyOrYearly === 'Yearly')
      && (planName === 'Standard' && !isYearly)
    ) previewProration = false

    router.push(`/update?priceId=${priceId}&newSubType=${newSubType}&previewProration=${previewProration}`)
  }

  const isSubDataYearly = subData.monthlyOrYearly === 'Yearly'
  
  const isMonthlyDisabled = planName === subData.tier && !isSubDataYearly
  const isYearlyDisabled = planName === subData.tier && isSubDataYearly

  return (
    <ButtonWrapper>
      <PrimaryBtn 
        disabled={isMonthlyDisabled}
        onClick={() => handleCheckout(false)}
      >
        {isMonthlyDisabled ? 'Current Plan' : monthlyPrice}
      </PrimaryBtn>
      <SecondaryBtn 
        disabled={isYearlyDisabled}
        onClick={() => handleCheckout(true)}
      >
        {isYearlyDisabled ? 'Current Plan' : yearlyPrice}
      </SecondaryBtn>
    </ButtonWrapper>
  )
}
